import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { 
  Trash2, 
  CheckCircle, 
  UserPlus, 
  X,
  AlertTriangle 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BulkActions = ({ 
  selectedTasks, 
  onClearSelection, 
  onActionComplete, 
  token,
  users = [],
  isSuperUser = false
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null });
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState('');

  const handleBulkStatus = async (status) => {
    setIsProcessing(true);
    try {
      const response = await fetch(`${API}/tasks/bulk/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          task_ids: selectedTasks,
          status
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        if (data.failed.length > 0) {
          toast.warning(`${data.failed.length} tasks could not be updated`);
        }
        onActionComplete();
        onClearSelection();
      } else {
        toast.error(data.detail || 'Failed to update tasks');
      }
    } catch (error) {
      console.error('Bulk status update error:', error);
      toast.error('Failed to update tasks');
    } finally {
      setIsProcessing(false);
      setConfirmDialog({ open: false, action: null });
    }
  };

  const handleBulkDelete = async () => {
    setIsProcessing(true);
    try {
      const response = await fetch(`${API}/tasks/bulk/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          task_ids: selectedTasks
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        if (data.failed.length > 0) {
          toast.warning(`${data.failed.length} tasks could not be deleted`);
        }
        onActionComplete();
        onClearSelection();
      } else {
        toast.error(data.detail || 'Failed to delete tasks');
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Failed to delete tasks');
    } finally {
      setIsProcessing(false);
      setConfirmDialog({ open: false, action: null });
    }
  };

  const handleBulkAssign = async () => {
    if (!selectedAssignee) {
      toast.error('Please select an assignee');
      return;
    }
    
    setIsProcessing(true);
    try {
      const response = await fetch(`${API}/tasks/bulk/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          task_ids: selectedTasks,
          assignee_id: selectedAssignee
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        if (data.failed.length > 0) {
          toast.warning(`${data.failed.length} tasks could not be reassigned`);
        }
        onActionComplete();
        onClearSelection();
      } else {
        toast.error(data.detail || 'Failed to reassign tasks');
      }
    } catch (error) {
      console.error('Bulk assign error:', error);
      toast.error('Failed to reassign tasks');
    } finally {
      setIsProcessing(false);
      setAssignDialogOpen(false);
      setSelectedAssignee('');
    }
  };

  if (selectedTasks.length === 0) return null;

  return (
    <>
      {/* Bulk Action Bar */}
      <div 
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-card border shadow-xl rounded-xl p-4 flex items-center gap-4"
        data-testid="bulk-action-bar"
      >
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-sm px-3 py-1">
            {selectedTasks.length} selected
          </Badge>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClearSelection}
            data-testid="clear-selection-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="h-6 w-px bg-border" />

        <div className="flex items-center gap-2">
          {/* Mark as Complete */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirmDialog({ open: true, action: 'complete' })}
            disabled={isProcessing}
            data-testid="bulk-complete-btn"
          >
            <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
            Mark Complete
          </Button>

          {/* Reassign */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAssignDialogOpen(true)}
            disabled={isProcessing}
            data-testid="bulk-assign-btn"
          >
            <UserPlus className="w-4 h-4 mr-2 text-blue-500" />
            Reassign
          </Button>

          {/* Delete */}
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setConfirmDialog({ open: true, action: 'delete' })}
            disabled={isProcessing}
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Confirmation Dialog for Complete/Delete */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ open, action: confirmDialog.action })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              {confirmDialog.action === 'delete' ? 'Delete Tasks' : 'Mark Tasks Complete'}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog.action === 'delete' 
                ? `Are you sure you want to delete ${selectedTasks.length} task(s)? This will also delete all sub-tasks, messages, and attachments. This action cannot be undone.`
                : `Are you sure you want to mark ${selectedTasks.length} task(s) as completed?`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setConfirmDialog({ open: false, action: null })}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              variant={confirmDialog.action === 'delete' ? 'destructive' : 'default'}
              onClick={() => confirmDialog.action === 'delete' ? handleBulkDelete() : handleBulkStatus('completed')}
              disabled={isProcessing}
              data-testid="confirm-bulk-action-btn"
            >
              {isProcessing ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign Tasks</DialogTitle>
            <DialogDescription>
              Select a new assignee for {selectedTasks.length} task(s).
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
              <SelectTrigger data-testid="bulk-assignee-select">
                <SelectValue placeholder="Select assignee" />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setAssignDialogOpen(false);
                setSelectedAssignee('');
              }}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkAssign}
              disabled={isProcessing || !selectedAssignee}
              data-testid="confirm-bulk-assign-btn"
            >
              {isProcessing ? 'Processing...' : 'Reassign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkActions;
